import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, BookOpen, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import Seo from "../components/Seo";
import Navbar from "../components/Navbar";

export default function Blog() {
  const [articles, setArticles] = useState(null);

  useEffect(() => {
    api("/articles")
      .then(setArticles)
      .catch(() => setArticles([]));
  }, []);

  return (
    <div className="min-h-screen bg-ink text-white overflow-x-clip">
      <Seo
        title="Nutrition & Diet Articles | Fitness Fanatic Nik"
        description="Evidence-based nutrition guides on fat loss, muscle gain, Indian dietetics and sustainable eating — by Certified Dietitian Nikhil Shrirao."
      />
      <div className="grain" aria-hidden="true" />
      <Navbar crossPage />
      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pt-40 pb-24">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-2xl">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-emerald-400 mb-4 flex items-center gap-3">
            <span className="h-px w-8 bg-emerald-400/60" />
            The Journal
          </p>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold uppercase tracking-tight leading-[0.95] text-white">
            Evidence-Based Nutrition, Decoded
          </h1>
          <p className="mt-4 text-slate-400 leading-relaxed">
            Practical dietetics for working professionals — fat loss science, macro guides and Indian kitchen
            strategies.
          </p>
        </motion.div>

        {articles === null ? (
          <div className="flex justify-center py-32">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-400" />
          </div>
        ) : articles.length === 0 ? (
          <div className="mt-16 rounded-3xl border border-dashed border-line bg-panel p-16 text-center" data-testid="blog-empty-state">
            <BookOpen className="h-10 w-10 text-slate-600 mx-auto" />
            <p className="mt-5 font-display text-xl font-bold uppercase text-white">First article coming soon</p>
            <p className="mt-2 text-sm text-slate-400">Nikhil is writing evidence-based guides — check back shortly.</p>
          </div>
        ) : (
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
            {articles.map((a, i) => (
              <motion.div
                key={a.id}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.55, delay: i * 0.07 }}
                className="group flex flex-col rounded-3xl border border-line bg-panel p-8 transition-all duration-300 hover:-translate-y-1.5 hover:border-accent/50 hover:shadow-glow-emerald"
                data-testid={`blog-card-${a.slug}`}
              >
                <div className="flex items-center justify-between">
                  <span className="px-3 py-1 rounded-full bg-gold/10 border border-gold/30 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-gold">
                    {a.category}
                  </span>
                  <span className="font-mono text-[10px] text-slate-600">
                    {a.created_at ? new Date(a.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : ""}
                  </span>
                </div>
                <h2 className="mt-5 font-display text-2xl font-extrabold uppercase text-white leading-snug">
                  {a.title}
                </h2>
                <p className="mt-3 text-sm text-slate-400 leading-relaxed flex-1 line-clamp-4">{a.excerpt}</p>
                <Link
                  to={`/blog/${a.slug}`}
                  data-testid={`blog-card-link-${a.slug}`}
                  className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-emerald-400 transition-colors hover:text-emerald-300"
                >
                  Read Article <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}